// ============================================================
// FeedbackPage.jsx — Exact React conversion of feedback.html
// Star rating, district/constituency, image upload, status modal
// ============================================================
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Assembly data — exact from feedback.html
const assemblyData = {
  Chennai: ['Harbour','R.K. Nagar','Perambur','Kolathur','Villivakkam','Thiru-Vi-Ka-Nagar','Egmore','Royapuram','Chepauk-Thiruvallikeni','Thousand Lights','Anna Nagar','Virugampakkam','Saidapet','T. Nagar','Mylapore','Velachery'],
  Coimbatore: ['Mettupalayam','Sulur','Kavundampalayam','Coimbatore North','Thondamuthur','Coimbatore South','Singanallur','Kinathukadavu','Pollachi','Valparai'],
  Erode: ['Erode East','Erode West','Modakkurichi','Perundurai','Bhavani','Anthiyur','Gobichettipalayam','Bhavanisagar'],
  Madurai: ['Melur','Madurai East','Madurai West','Madurai North','Madurai South','Madurai Central','Thirupparankundram','Tirumangalam','Usilampatti'],
  Salem: ['Edappadi','Mettur','Omalur','Salem North','Salem South','Salem West','Veerapandi','Yercaud'],
  Tiruchirappalli: ['Srirangam','Tiruchirappalli West','Tiruchirappalli East','Thiruverumbur','Lalgudi','Manachanallur','Musiri','Thuraiyur'],
  Tirunelveli: ['Tirunelveli','Palayamkottai','Ambasamudram','Nanguneri','Radhapuram'],
  Vellore: ['Gudiyatham','Katpadi','Vellore','Anaikattu','Kilvaithinankuppam'],
  Dharmapuri: ['Dharmapuri','Pennagaram','Palacode','Pappireddippatti'],
  Namakkal: ['Rasipuram','Senthamangalam','Namakkal','Paramathi Velur','Tiruchengode','Kumarapalayam'],
  Dindigul: ['Palani','Oddanchatram','Athoor','Nilakkottai','Natham','Dindigul'],
  Thanjavur: ['Orathanadu','Papanasam','Thiruvaiyaru','Thanjavur','Pattukkottai','Peravurani'],
  Nagapattinam: ['Kilvelur','Nagapattinam','Vedaranyam'],
  Cuddalore: ['Panruti','Kurinjipadi','Bhuvanagiri','Chidambaram','Kattumannarkoil','Cuddalore','Neyveli'],
  Villupuram: ['Ulundurpettai','Rishivandiyam','Sankarapuram','Villupuram','Vanur','Tindivanam'],
  Kanchipuram: ['Uthiramerur','Kanchipuram','Sriperumbudur'],
  Tiruvallur: ['Gummidipoondi','Ponneri','Tiruttani','Thiruvallur','Poonamallee','Avadi','Madavaram'],
  Krishnagiri: ['Krishnagiri','Bargur','Hosur','Thalli','Uthangarai'],
  Thoothukudi: ['Thoothukudi','Tiruchendur','Srivaikuntam','Ottapidaram','Vilathikulam'],
  Virudhunagar: ['Rajapalayam','Srivilliputhur','Sattur','Sivakasi','Virudhunagar','Aruppukkottai'],
  Ramanathapuram: ['Paramakudi','Tiruvadanai','Ramanathapuram','Mudhukulathur'],
  Sivagangai: ['Sivaganga','Karaikudi','Tirupattur','Manamadurai'],
  Tenkasi: ['Tenkasi','Alangulam','Kadayanallur','Sankarankovil','Vasudevanallur'],
  Karur: ['Karur','Krishnarayapuram','Aravakurichi','Kulithalai'],
  Ariyalur: ['Ariyalur','Jayankondam'],
  Perambalur: ['Perambalur','Kunnam'],
  Theni: ['Bodinayakanur','Cumbum','Andipatti','Periyakulam'],
  Nilgiris: ['Ooty','Coonoor','Gudalur'],
};

export default function FeedbackPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [phone, setPhone] = useState('');
  const [district, setDistrict] = useState('');
  const [constituency, setConstituency] = useState('');
  const [constituencies, setConstituencies] = useState([]);
  const [category, setCategory] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [whatsappNotify, setWhatsappNotify] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Status modal state — mirrors original showStatus/closeStatus
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('success'); // 'success' | 'error'
  const [modalTitle, setModalTitle] = useState('');
  const [modalDesc, setModalDesc] = useState('');
  const [modalAnimOut, setModalAnimOut] = useState(false);

  // ─── showStatus — exact mirror from feedback.html ───────────
  function showStatus(type, title, desc) {
    setModalType(type);
    setModalTitle(title);
    setModalDesc(desc);
    setModalAnimOut(false);
    setModalVisible(true);
  }

  // ─── closeStatus — exact mirror from feedback.html ──────────
  function closeStatus() {
    setModalAnimOut(true);
    setTimeout(() => setModalVisible(false), 450);
  }

  // ─── District change → update constituencies ────────────────
  function handleDistrictChange(val) {
    setDistrict(val);
    setConstituency('');
    setConstituencies(assemblyData[val] || []);
  }

  // ─── WhatsApp radio + phone focus — exact from feedback.html ─
  function handleWhatsappChange(val) {
    setWhatsappNotify(val);
    if (val === 'yes') {
      document.getElementById('phone')?.focus();
    }
  }

  // ─── Image upload + preview — exact from feedback.html ──────
  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      showStatus('error', 'File Too Large', 'Maximum limit is 10MB.');
      e.target.value = '';
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  }

  function removePreview() {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ─── submitFeedback — exact mirror from feedback.html ───────
  async function submitFeedback(e) {
    e.preventDefault();
    const email = localStorage.getItem('user');

    if (!district || !constituency || !category || !feedbackText || !phone) {
      showStatus('error', 'Incomplete Form', 'Please fill in all required fields.');
      return;
    }

    const formData = new FormData();
    formData.append('district', district);
    formData.append('constituency', constituency);
    formData.append('booth_no', phone);
    formData.append('email', email);
    formData.append('type_of_feedback', category);
    formData.append('feedback_text', feedbackText);
    formData.append('rating', rating);
    if (imageFile) formData.append('image', imageFile);

    try {
      const res = await fetch('http://127.0.0.1:8000/api/feedback', {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        showStatus('success', 'Feedback Sent', 'Your Feedback have been successfully submitted.');
        setFeedbackText('');
        removePreview();
      } else {
        showStatus('error', 'Server Rejected', 'The backend received the data but could not save it.');
      }
    } catch (err) {
      showStatus('error', 'Sync Failed', 'Could not connect to the server. Check if your Python API is running.');
    }
  }

  return (
    // ─── feedback.html body structure — exact ────────────────────
    <div
      className="font-body min-h-screen relative overflow-x-hidden text-[#0f291b] page-feedback-body sidebar-page"
      style={{ fontFamily: "'Manrope', sans-serif", background: 'linear-gradient(135deg, #bbf7d0 0%, #86efac 100%)' }}
    >
      {/* Background Blobs — exact from feedback.html */}
      <div className="blob blob-1"></div>
      <div className="blob blob-2"></div>

      <div className="layout-container flex flex-col min-h-screen">

        {/* Header — exact from feedback.html */}
        <header className="flex items-center justify-between whitespace-nowrap border-b border-solid px-10 py-4 glass-card sticky top-0 z-50 border-white/10">
          <div className="flex items-center gap-4 text-charcoal">
            <div className="size-6">
              <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M36.7273 44C33.9891 44 31.6043 39.8386 30.3636 33.69C29.123 39.8386 26.7382 44 24 44C21.2618 44 18.877 39.8386 17.6364 33.69C16.3957 39.8386 14.0109 44 11.2727 44C7.25611 44 4 35.0457 4 24C4 12.9543 7.25611 4 11.2727 4C14.0109 4 16.3957 8.16144 17.6364 14.31C18.877 8.16144 21.2618 4 24 4C26.7382 4 29.123 8.16144 30.3636 14.31C31.6043 8.16144 33.9891 4 36.7273 4C40.7439 4 44 12.9543 44 24C44 35.0457 40.7439 44 36.7273 44Z"
                  fill="#15803d"
                ></path>
              </svg>
            </div>
            <h2 className="text-lg font-display font-bold leading-tight tracking-tight text-[#0f291b]">
              Corporate Feedback
            </h2>
          </div>
          <div className="flex flex-1 justify-end gap-8">
            <nav className="hidden md:flex items-center gap-9">
              <a
                className="text-sm font-semibold hover:text-[#15803d] transition-colors text-[#0f291b]"
                href="#"
                onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}
              >
                Dashboard
              </a>
              <a className="text-sm font-semibold hover:text-[#15803d] transition-colors text-[#0f291b]" href="#">Insights</a>
              <a className="text-sm font-semibold hover:text-[#15803d] transition-colors text-[#0f291b]" href="#">Resources</a>
            </nav>
            <div className="flex gap-2">
              <button className="flex items-center justify-center rounded-xl h-10 w-10 bg-white/50 backdrop-blur-md border border-white/20 btn-glass">
                <span className="material-symbols-outlined text-gray-500 hover:text-gray-700 transition">notifications</span>
              </button>
              <button className="flex items-center justify-center rounded-xl h-10 w-10 bg-white/50 backdrop-blur-md border border-white/20 btn-glass">
                <span className="material-symbols-outlined text-gray-500 hover:text-gray-700 transition">account_circle</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main — exact from feedback.html */}
        <main className="flex-1 relative py-24 overflow-hidden flex justify-center">
          {/* Background images — exact from feedback.html */}
          <img
            src="/stars.png"
            className="absolute left-[-20px] bottom-[-50px] w-[40vw] max-w-[400px] opacity-60 pointer-events-none"
            style={{ zIndex: 1 }}
            alt="Stars Background"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <img
            src="/both.png"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] max-w-[700px] opacity-40 pointer-events-none blur-[1px]"
            style={{ zIndex: 1 }}
            alt="Background 3D"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <img
            src="/phone2.png"
            className="hidden lg:block absolute right-4 top-10 w-[380px] drop-shadow-2xl z-0 pointer-events-none"
            alt="3D Phone"
            onError={(e) => { e.target.style.display = 'none'; }}
          />

          <div className="w-full max-w-[800px] flex flex-col gap-8 relative z-10">
            {/* Title — exact from feedback.html */}
            <div className="flex flex-col gap-2 text-center md:text-left">
              <h1 className="text-6xl md:text-7xl font-extrabold tracking-tight leading-tight font-display text-[#0f291b]">
                Share Your Insight
              </h1>
              <p className="text-lg font-body text-[#052e16] font-medium opacity-85 leading-relaxed max-w-2xl">
                Help us shape the future of our culture. Your feedback is powerful and completely confidential.
              </p>
            </div>

            {/* Form Card — exact from feedback.html */}
            <div className="glass-card rounded-xl p-12 shadow-2xl overflow-hidden relative mx-auto w-full"
              style={{ background: 'rgba(240, 253, 244, 0.65)', border: '1px solid rgba(22, 163, 74, 0.25)' }}>
              <div className="flex flex-col gap-8 relative z-10">

                {/* Form Header - Dark and Light Combo Box */}
                <div className="flex items-center gap-4 bg-[#15803d] p-6 rounded-t-xl -mx-12 -mt-12 mb-4 shadow-md">
                  <div className="p-3 rounded-lg bg-white/20 text-white">
                    <span className="material-symbols-outlined text-3xl">rate_review</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-display font-bold text-white">Feedback Details</h3>
                    <p className="text-sm text-emerald-100 font-medium">Describe your experience or suggestions below.</p>
                  </div>
                </div>

                <div className="flex flex-col gap-6">
                  {/* Star Rating — exact from feedback.html */}
                  <label className="text-sm font-display font-bold uppercase tracking-[2px] text-[#0f291b] text-center md:text-left">
                    How are you feeling?
                  </label>
                  <div className="flex justify-center gap-3 text-4xl cursor-pointer star-rating">
                    {[1, 2, 3, 4, 5].map((val) => (
                      <span
                        key={val}
                        data-value={val}
                        className={`star transition-all duration-300 ${
                          val <= (hoverRating || rating) ? 'text-yellow-400' : 'text-gray-300'
                        }`}
                        style={{
                          transform: val <= (hoverRating || rating) ? 'scale(1.2)' : 'scale(1)',
                        }}
                        onClick={() => setRating(val)}
                        onMouseEnter={() => setHoverRating(val)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        ★
                      </span>
                    ))}
                  </div>

                  {/* Phone + District Grid — exact from feedback.html */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-display font-bold uppercase tracking-wider text-[#14532d]">
                        Phone Number
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 9762549478"
                        required
                        className="w-full rounded-xl border border-[#16a34a]/20 bg-emerald-50/50 backdrop-blur-md text-[#0f291b] p-4 font-body focus:ring-2 focus:ring-[#15803d]/30 transition-all outline-none"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-display font-bold uppercase tracking-wider text-[#14532d]">
                        District (TN)
                      </label>
                      <div className="relative">
                        <select
                          id="district"
                          value={district}
                          onChange={(e) => handleDistrictChange(e.target.value)}
                          className="appearance-none w-full rounded-xl border border-[#16a34a]/20 bg-emerald-50/50 backdrop-blur-md text-[#0f291b] p-4 font-body focus:ring-2 focus:ring-[#15803d]/30 transition-all outline-none"
                        >
                          <option value="">Select District</option>
                          {Object.keys(assemblyData).map((d) => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                          <span className="material-symbols-outlined text-[#6B7280]">expand_more</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Constituency — exact from feedback.html */}
                  <div className="grid grid-cols-1 gap-6 mt-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-display font-bold uppercase tracking-wider text-[#14532d]">
                        Assembly Constituency
                      </label>
                      <div className="relative">
                        <select
                          id="constituency"
                          value={constituency}
                          onChange={(e) => setConstituency(e.target.value)}
                          className="appearance-none w-full rounded-xl border border-[#16a34a]/20 bg-emerald-50/50 backdrop-blur-md text-[#0f291b] p-4 font-body focus:ring-2 focus:ring-[#15803d]/30 transition-all outline-none"
                        >
                          <option value="">Select district first</option>
                          {constituencies.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                          <span className="material-symbols-outlined text-[#6B7280]">expand_more</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Category — exact from feedback.html */}
                  <div className="grid grid-cols-1 gap-6 mt-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-display font-bold uppercase tracking-wider text-[#14532d]">
                        Feedback Category
                      </label>
                      <div className="relative">
                        <select
                          id="category"
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          className="appearance-none w-full rounded-xl border border-[#16a34a]/20 bg-emerald-50/50 backdrop-blur-md text-[#0f291b] p-4 font-body focus:ring-2 focus:ring-[#15803d]/30 transition-all outline-none"
                        >
                          <option value="" className="text-[#6B7280]">Select a category</option>
                          <option value="road">Roads & Infrastructure</option>
                          <option value="power">Electricity & Power</option>
                          <option value="water">Water Supply</option>
                          <option value="security">Public Security</option>
                          <option value="other">Sanitation</option>
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                          <span className="material-symbols-outlined text-[#6B7280]">expand_more</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Textarea — exact from feedback.html */}
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-display font-bold uppercase tracking-wider text-[#14532d]">
                      Your Message
                    </label>
                    <textarea
                      id="feedback"
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      className="w-full min-h-[180px] font-body rounded-xl border border-[#16a34a]/20 bg-emerald-50/50 backdrop-blur-md text-[#0f291b] p-4 focus:ring-2 focus:ring-[#15803d]/30 transition-all outline-none placeholder:text-[#4b6b58]/50"
                      placeholder="Describe your feedback in detail... What happened? How can we improve?"
                    />
                    <div className="flex justify-end mt-1">
                      <span className="text-[10px] text-[#6B7280]/40 uppercase font-display font-bold">
                        Max 2000 characters
                      </span>
                    </div>
                  </div>

                  {/* File Upload — exact from feedback.html */}
                  <div className="flex flex-col gap-2">
                    <label className="text-[#6B7280] text-sm font-display font-semibold uppercase tracking-wider text-center">
                      Supporting Documentation (Optional)
                    </label>
                    <div
                      className="border-2 border-dashed border-[#15803d]/30 rounded-xl p-8 flex flex-col items-center justify-center gap-2 bg-emerald-50/30 backdrop-blur-md hover:bg-[#15803d]/10 transition-colors cursor-pointer group"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <span className="material-symbols-outlined text-4xl group-hover:scale-110 transition-transform">
                        cloud_upload
                      </span>
                      <div className="text-center font-body">
                        <p className="text-sm font-medium text-[#46464e]">
                          Drag & drop files here or{' '}
                          <span className="underline cursor-pointer">browse</span>
                        </p>
                        <input
                          type="file"
                          id="fileUpload"
                          ref={fileInputRef}
                          className="hidden"
                          onChange={handleFileChange}
                          accept="image/*,.pdf"
                        />
                        <p className="text-xs text-[#7b7e84]">PDF, JPG, PNG (Max 10MB)</p>
                      </div>
                    </div>
                    {/* Image Preview — exact from feedback.html */}
                    {imagePreview && (
                      <div
                        id="previewContainer"
                        className="mt-6 p-4 glass-card rounded-2xl border border-white/40 flex flex-col items-center gap-3 animate-in fade-in zoom-in duration-500"
                      >
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">
                          Selected Image Preview
                        </p>
                        <img
                          src={imagePreview}
                          className="max-w-full h-48 object-cover rounded-xl shadow-2xl border-2 border-white/50"
                          alt="Preview"
                        />
                        <button
                          type="button"
                          onClick={removePreview}
                          className="text-xs text-red-500 font-bold hover:underline mt-2"
                        >
                          Remove Image
                        </button>
                      </div>
                    )}
                  </div>

                  {/* WhatsApp Notification — exact from feedback.html */}
                  <div className="flex flex-col gap-4 mt-6">
                    <label className="text-sm font-display font-bold uppercase tracking-wider text-[#14532d]">
                      Resolution Notification
                    </label>
                    <p className="text-xs text-[#6B7280]">
                      Would you like to receive a WhatsApp notification when your issue is resolved?
                    </p>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="whatsappNotify"
                          value="yes"
                          className="accent-gray-600 w-4 h-4"
                          checked={whatsappNotify === 'yes'}
                          onChange={() => handleWhatsappChange('yes')}
                        />
                        <span className="text-sm">Yes, notify me on WhatsApp</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="whatsappNotify"
                          value="no"
                          className="accent-gray-600 w-4 h-4"
                          checked={whatsappNotify === 'no'}
                          onChange={() => handleWhatsappChange('no')}
                        />
                        <span className="text-sm">No notification needed</span>
                      </label>
                    </div>
                  </div>

                  {/* Submit Button — exact from feedback.html */}
                  <div className="flex flex-col items-center pt-4">
                    <button
                      onClick={submitFeedback}
                      className="px-10 py-4 rounded-full bg-[#15803d] text-white font-display font-bold tracking-wide shadow-xl hover:shadow-2xl hover:bg-[#166534] hover:-translate-y-1 transition-all duration-300"
                    >
                      Send Feedback
                    </button>
                    <p className="text-[11px] text-[#4b6b58]/60 mt-4 max-w-sm text-center font-body text-[#4b6b58]">
                      By submitting, you agree to our Terms of Service and recognize that your feedback will be reviewed by the Ethics Committee.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Cards — green variations styling */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl glass-card flex items-center gap-4">
                <div className="size-10 rounded-lg bg-green-500/10 flex items-center justify-center text-green-600">
                  <span className="material-symbols-outlined">verified_user</span>
                </div>
                <div>
                  <p className="text-xs font-display font-bold text-[#14532d]">Secure</p>
                  <p className="text-xs font-display font-bold text-[#4b6b58]">End-to-end encrypted</p>
                </div>
              </div>
              <div className="p-4 rounded-xl glass-card flex items-center gap-4">
                <div className="size-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600">
                  <span className="material-symbols-outlined">history</span>
                </div>
                <div>
                  <p className="text-xs font-display font-bold text-[#14532d]">History</p>
                  <p className="text-xs font-display font-bold text-[#4b6b58]">Track your progress</p>
                </div>
              </div>
              <div className="p-4 rounded-xl glass-card flex items-center gap-4">
                <div className="size-10 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-600">
                  <span className="material-symbols-outlined">support_agent</span>
                </div>
                <div>
                  <p className="text-xs font-display font-bold text-[#14532d]">Support</p>
                  <p className="text-xs font-display font-bold text-[#4b6b58]">24/7 internal assistance</p>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer — exact from feedback.html */}
        <footer className="py-10 px-10 glass-card mt-auto">
          <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2 text-primary/60">
              <span className="material-symbols-outlined text-[18px] text-[#6B7280]/60">copyright</span>
              <span className="text-sm font-medium font-body text-[#6B7280]/60">2026 Corporate Feedback</span>
            </div>
            <div className="flex gap-8">
              <a className="text-sm hover:text-primary transition-colors font-body text-[#6B7280]/60" href="#">Help Center</a>
              <a className="text-sm hover:text-primary transition-colors font-body text-[#6B7280]/60" href="#">Data Processing</a>
              <a className="text-sm hover:text-primary transition-colors font-body text-[#6B7280]/60" href="#">Legal Notice</a>
            </div>
          </div>
        </footer>
      </div>

      {/* ── Status Overlay Modal — exact from feedback.html ── */}
      {modalVisible && (
        <div id="statusOverlay" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            id="statusBlur"
            className="absolute inset-0 bg-black/20 backdrop-blur-md"
            onClick={closeStatus}
          ></div>
          <div
            id="statusCard"
            className={`relative w-full max-w-md p-10 rounded-[2.5rem] border border-white/40 shadow-2xl flex flex-col items-center gap-8 text-center transition-all duration-500 ${
              modalType === 'success' ? 'glass-success' : 'glass-error'
            } ${modalAnimOut ? 'modal-outro' : 'scale-100 opacity-100'}`}
          >
            <div id="iconBox" className="size-24 rounded-full flex items-center justify-center shadow-inner relative overflow-hidden icon-pop">
              {modalType === 'success' ? (
                <svg id="svgSuccess" className="size-16" viewBox="0 0 52 52">
                  <circle cx="26" cy="26" r="25" fill="none" stroke="#10B981" strokeWidth="2" />
                  <path className="checkmark-path" fill="none" stroke="#10B981" strokeWidth="4" strokeLinecap="round" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                </svg>
              ) : (
                <svg id="svgError" className="size-16" viewBox="0 0 52 52">
                  <circle cx="26" cy="26" r="25" fill="none" stroke="#EF4444" strokeWidth="2" />
                  <path className="checkmark-path" fill="none" stroke="#EF4444" strokeWidth="4" strokeLinecap="round" d="M16 16l20 20M36 16L16 36" />
                </svg>
              )}
            </div>
            <div>
              <h3 id="statusTitle" style={{ color: '#0f291b', fontWeight: 900 }} className="text-3xl font-display tracking-tight">
                {modalTitle}
              </h3>
              <p id="statusDesc" style={{ color: '#374151', fontWeight: 600 }} className="mt-3 font-body leading-relaxed">
                {modalDesc}
              </p>
            </div>
            <button
              id="statusBtn"
              onClick={closeStatus}
              className={`w-full py-5 rounded-[1.5rem] font-bold text-white shadow-xl transition-all active:scale-95 hover:brightness-110 tracking-widest uppercase text-xs ${
                modalType === 'success'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600'
                  : 'bg-gradient-to-r from-red-500 to-rose-600'
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
